<template>
  <BasicModal
    title="面接一覧"
    width="1200px"
    :canCancel="canCancel"
    :isShowModal="isShowModal"
    @cancel="onCancel"
  >
    <div style="padding: 0px 24px 24px 24px;">
      <!-- フィルター部分 -->
      <div class="filter-section" style="margin-bottom: 20px;">
        <div class="field is-grouped">
          <p class="control">
            <input 
              v-model="search" 
              class="input is-small" 
              type="text" 
              placeholder="応募者名で検索"
              @input="debounceSearch"
            >
          </p>
          <p class="control">
            <div class="select is-small">
              <select v-model="statusFilter" @change="loadInterviews">
                <option value="all">すべて</option>
                <option value="upcoming">予定</option>
                <option value="past">実施済み</option>
              </select>
            </div>
          </p>
          <p class="control">
            <b-datepicker
              v-model="startDate"
              placeholder="開始日"
              size="is-small"
              @input="loadInterviews"
            />
          </p>
          <p class="control">
            <b-datepicker
              v-model="endDate"
              placeholder="終了日"
              size="is-small"
              @input="loadInterviews"
            />
          </p>
          <p class="control">
            <button class="button is-small is-primary" @click="loadInterviews">
              <span class="mdi mdi-magnify"></span>検索
            </button>
          </p>
          <p class="control">
            <button class="button is-small" @click="resetFilter">
              <span class="mdi mdi-refresh"></span>リセット
            </button>
          </p>
        </div>
      </div>

      <!-- テーブル部分 -->
      <div class="table-container">
        <table class="table is-fullwidth is-striped">
          <thead>
            <tr>
              <th @click="changeSort('seeker__name')" class="is-clickable">
                応募者名
                <span class="mdi" :class="getSortIcon('seeker__name')"></span>
              </th>
              <th>電話番号</th>
              <th>メールアドレス</th>
              <th @click="changeSort('interview_start')" class="is-clickable">
                面接日時
                <span class="mdi" :class="getSortIcon('interview_start')"></span>
              </th>
              <th>コメント</th>
              <th>ステータス</th>
              <th @click="changeSort('created_at')" class="is-clickable">
                登録日時
                <span class="mdi" :class="getSortIcon('created_at')"></span>
              </th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="has-text-centered">
                <b-loading :is-full-page="false" :active="loading"></b-loading>
                読み込み中...
              </td>
            </tr>
            <tr v-else-if="interviews.length === 0">
              <td colspan="8" class="has-text-centered">
                面接予定がありません
              </td>
            </tr>
            <tr v-else v-for="interview in interviews" :key="interview.id">
              <td>
                <a @click="openSeekerDetail(interview.seeker.id)" class="has-text-link is-clickable">
                  {{ interview.seeker.name }}
                </a>
              </td>
              <td>{{ interview.seeker.tel || '-' }}</td>
              <td>{{ interview.seeker.email || '-' }}</td>
              <td>{{ formatDateTime(interview.interview_start) }}</td>
              <td>{{ interview.interview_comment || '-' }}</td>
              <td>
                <span 
                  class="tag"
                  :class="interview.status === 'upcoming' ? 'is-primary' : 'is-light'"
                >
                  {{ interview.status === 'upcoming' ? '予定' : '実施済み' }}
                </span>
              </td>
              <td>{{ formatDateTime(interview.created_at) }}</td>
              <td>
                <button 
                  class="button is-small is-danger" 
                  @click="confirmDeleteInterview(interview)"
                >
                  <span class="mdi mdi-delete"></span>削除
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- ページネーション -->
        <nav class="pagination is-centered" role="navigation" v-if="totalCount > perPage">
          <b-pagination
            v-model="currentPage"
            :total="totalCount"
            :per-page="perPage"
            :range-before="3"
            :range-after="3"
            size="is-small"
            @change="loadInterviews"
          />
        </nav>
      </div>
    </div>

    <!-- 削除確認ダイアログ -->
    <b-modal 
      v-model="showDeleteConfirm" 
      has-modal-card 
      trap-focus 
      :width="400"
    >
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">削除確認</p>
        </header>
        <section class="modal-card-body">
          <p>この面接予定を削除してもよろしいですか？</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="showDeleteConfirm = false">
            キャンセル
          </button>
          <button class="button is-danger" @click="deleteInterview">
            削除
          </button>
        </footer>
      </div>
    </b-modal>
  </BasicModal>
</template>

<script>
import BasicModal from '@/components/Modals/BasicModal'
import moment from 'moment'
import _ from 'lodash'
import { mapActions } from 'vuex'

export default {
  name: 'InterviewListModal',
  
  components: {
    BasicModal
  },
  
  props: {
    isShowModal: {
      type: Boolean,
      required: true
    },
    canCancel: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      loading: false,
      interviews: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 20,
      search: '',
      statusFilter: 'all',
      startDate: null,
      endDate: null,
      sortBy: 'created_at',
      sortDesc: true,
      showDeleteConfirm: false,
      selectedInterview: null
    }
  },
  
  watch: {
    isShowModal(val) {
      if (val) {
        this.resetFilter()
        this.loadInterviews()
      }
    }
  },
  
  methods: {
    ...mapActions('directtalk', [
      'fetchInterviewList',
      'deleteInterviewHistory'
    ]),
    
    async loadInterviews() {
      this.loading = true
      
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage,
          search: this.search
        }
        
        // ステータスフィルター
        if (this.statusFilter !== 'all') {
          params.status = this.statusFilter
        }
        
        // 日付フィルター
        if (this.startDate) {
          params.start_date = moment(this.startDate).format('YYYY-MM-DD')
        }
        if (this.endDate) {
          params.end_date = moment(this.endDate).format('YYYY-MM-DD')
        }
        
        // ソート
        if (this.sortBy) {
          params.ordering = this.sortDesc ? `-${this.sortBy}` : this.sortBy
        }
        
        const response = await this.fetchInterviewList(params)
        // 古いAPIレスポンス形式に対応
        this.interviews = response.interviews || response.results || []
        this.totalCount = response.count || response.total_count || 0
      } catch (error) {
        console.error('面接一覧の取得に失敗しました', error)
        this.$buefy.notification.open({
          message: '面接一覧の取得に失敗しました',
          type: 'is-danger',
          position: 'is-bottom-right'
        })
      } finally {
        this.loading = false
      }
    },
    
    debounceSearch: _.debounce(function() {
      this.currentPage = 1
      this.loadInterviews()
    }, 500),
    
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      return moment(dateStr).format('YYYY/MM/DD HH:mm')
    },
    
    changeSort(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc
      } else {
        this.sortBy = field
        this.sortDesc = false
      }
      this.loadInterviews()
    },
    
    getSortIcon(field) {
      if (this.sortBy !== field) {
        return 'mdi-sort'
      }
      return this.sortDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending'
    },
    
    resetFilter() {
      this.search = ''
      this.statusFilter = 'all'
      this.startDate = null
      this.endDate = null
      this.currentPage = 1
      this.sortBy = 'created_at'
      this.sortDesc = true
      this.loadInterviews()
    },
    
    openSeekerDetail(seekerId) {
      // 応募者詳細をイベントで親コンポーネントに通知
      this.$emit('open-seeker-detail', seekerId)
    },
    
    confirmDeleteInterview(interview) {
      this.selectedInterview = interview
      this.showDeleteConfirm = true
    },
    
    async deleteInterview() {
      if (!this.selectedInterview) return
      
      try {
        await this.deleteInterviewHistory({
          id: this.selectedInterview.seeker.id,
          start: this.selectedInterview.interview_start
        })
        this.$buefy.notification.open({
          message: '面接予定を削除しました',
          type: 'is-success',
          position: 'is-bottom-right'
        })
        this.showDeleteConfirm = false
        this.selectedInterview = null
        this.loadInterviews()
      } catch (error) {
        console.error('削除に失敗しました', error)
        this.$buefy.notification.open({
          message: '削除に失敗しました',
          type: 'is-danger',
          position: 'is-bottom-right'
        })
      }
    },
    
    onCancel() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.filter-section {
  padding: 10px 0;
  border-bottom: 1px solid #e8e8e8;
}

.table-container {
  margin-top: 20px;
}

.is-clickable {
  cursor: pointer;
}

.is-clickable:hover {
  background-color: #f5f5f5;
}

.content-preview {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>


// WEBPACK FOOTER //
// src/components/organisms/contents/InterviewListModal.vue